"use client";

import { LoaderCircle } from "lucide-react";
import { Suspense } from "react";
import type { TherapyDirectoryFilters } from "~/lib/therapy-directory-filters";
import DirectoryFilterPanel from "./directory-filter-panel";
import DirectoryModeTabs from "./directory-mode-tabs";
import DirectoryTopicModeBar from "./directory-topic-mode-bar";
import SortDropdown from "./sort-dropdown";
import { useDirectoryFilterNavigation } from "./use-directory-filter-navigation";

interface DirectoryToolbarProps {
  resultCount: number;
  filters?: TherapyDirectoryFilters;
  topic?: {
    slug: string;
    label: string;
  };
  showTopicSearch?: boolean;
  // Whether this route has no individual-therapy equivalent to switch to
  // (e.g. /terapija/za-parove). Narrower than the isCouplesTherapy the page
  // uses for session type/pricing: ?couples=1 on an ordinary specialty is
  // reversible via the mode tabs and must not hide them.
  isCouplesOnlyRoute: boolean;
}

export default function DirectoryToolbar({
  resultCount,
  filters,
  topic,
  showTopicSearch = false,
  isCouplesOnlyRoute,
}: DirectoryToolbarProps) {
  const { isPending, updateFilters } = useDirectoryFilterNavigation();
  const topicModeBar =
    filters && (topic || showTopicSearch) ? (
      <DirectoryTopicModeBar
        filters={filters}
        topic={topic}
        isCouplesOnlyRoute={isCouplesOnlyRoute}
        isPending={isPending}
        onUpdate={updateFilters}
        showFilters
      />
    ) : null;
  const modeTabs = filters ? (
    <DirectoryModeTabs
      filters={filters}
      isCouplesOnlyRoute={isCouplesOnlyRoute}
      isPending={isPending}
      onUpdate={updateFilters}
    />
  ) : null;

  return (
    <div className="mb-6">
      {(topic || showTopicSearch) && <div className="mb-5">{topicModeBar}</div>}

      {!topic && !showTopicSearch && modeTabs}

      <div className="relative flex flex-wrap items-center gap-x-3 gap-y-2 pt-1">
        <p className="order-2 flex items-center gap-2 text-sm font-medium tracking-tight text-foreground md:order-0">
          {resultCount} {resultCount === 1 ? "terapeut" : "terapeuta"}
          {isPending && (
            <LoaderCircle
              className="size-4 animate-spin text-brand"
              aria-label="Učitavanje rezultata"
            />
          )}
        </p>

        {filters && (
          <DirectoryFilterPanel
            filters={filters}
            isPending={isPending}
            onUpdate={updateFilters}
          />
        )}

        {filters && (
          <div className="order-4 ml-auto md:order-0">
            <Suspense>
              <SortDropdown />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  );
}
